/**
 * Lexicon — Speaker Profile (Baseball Card)
 *
 * Full profile view for a single speaker. Shows aggregated NLP scores,
 * pattern breakdown, coaching insights, and transcript history.
 *
 * Route: /prioritization/lexicon/profile/:speakerName
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap,
  TrendingUp, Brain, RefreshCw,
  AlertCircle, BookOpen, Star, AlertTriangle, Lightbulb,
} from 'lucide-react';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import {
  getSpeakerProfile,
  listTranscripts,
  getAnalysesForTranscript,
} from '../../../lib/lexiconService';
import type {
  SpeakerProfile,
  SpeakerAnalysis,
  LexiconTranscript,
  DetectedPattern,
} from '../../../types/prioritization/lexicon';
import {
  ARCHETYPE_DESCRIPTIONS,
} from '../../../types/prioritization/lexicon';

// ── Dimension config ──────────────────────────────────────────────────────────

interface DimConfig {
  key: keyof Pick<SpeakerProfile,
    'avgSpecificity' | 'avgClarity' | 'avgFlexibility' |
    'avgAgency' | 'avgMomentum' | 'avgStrategicIndex'
  >;
  abbr: string;
  label: string;
  description: string;
  category: string;
}

const DIMENSIONS: DimConfig[] = [
  {
    key: 'avgStrategicIndex',
    abbr: 'STR',
    label: 'Strategic Index',
    description: 'Big-picture outcome framing vs. operational/tactical detail',
    category: 'Derived',
  },
  {
    key: 'avgMomentum',
    abbr: 'MOM',
    label: 'Momentum',
    description: 'Action and commitment density across speaking turns',
    category: 'Derived',
  },
  {
    key: 'avgAgency',
    abbr: 'AGN',
    label: 'Agency',
    description: 'Ownership and internal locus of control language patterns',
    category: 'Derived',
  },
  {
    key: 'avgSpecificity',
    abbr: 'SPE',
    label: 'Specificity',
    description: 'Concrete, referenced language vs. deletions (unspecified nouns/verbs)',
    category: 'Deletion',
  },
  {
    key: 'avgClarity',
    abbr: 'CLR',
    label: 'Clarity',
    description: 'Freedom from distortion patterns (mind reading, cause-effect, presuppositions)',
    category: 'Distortion',
  },
  {
    key: 'avgFlexibility',
    abbr: 'FLX',
    label: 'Flexibility',
    description: 'Avoidance of rigid generalizations (always/never, must/can\'t)',
    category: 'Generalization',
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number | null): string {
  if (score === null) return 'text-[var(--text-ter)]';
  if (score >= 75) return 'text-[var(--green)] dark:text-[var(--text-ter)]';
  if (score >= 50) return 'text-[var(--text-ter)]';
  return 'text-[var(--mustard)]';
}

function barColor(score: number | null): string {
  if (score === null) return 'bg-[var(--hover-tint)]';
  if (score >= 75) return 'bg-[var(--green)]';
  if (score >= 50) return 'bg-[var(--slate)]';
  return 'bg-[var(--mustard)]';
}

function ScoreMeter({
  value,
  label,
  description,
  abbr,
}: {
  value: number | null;
  label: string;
  description: string;
  abbr: string;
}) {
  const pct = value ?? 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium text-[var(--text-pri)]">{label}</span>
          <span className="ml-2 text-xs text-[var(--text-ter)] font-mono">{abbr}</span>
        </div>
        <span className={`text-lg font-bold font-mono ${scoreColor(value)}`}>
          {value ?? '—'}
        </span>
      </div>
      <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor(value)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-[var(--text-ter)]">{description}</p>
    </div>
  );
}

function PatternSection({ patterns }: { patterns: DetectedPattern[] }) {
  const byCategory = {
    deletion:       patterns.filter(p => p.category === 'deletion'),
    distortion:     patterns.filter(p => p.category === 'distortion'),
    generalization: patterns.filter(p => p.category === 'generalization'),
  };

  const categoryConfig = {
    deletion:       { label: 'Deletion Patterns',       color: 'bg-[var(--slate-dim)] border-[var(--slate)]', badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    distortion:     { label: 'Distortion Patterns',     color: 'bg-[var(--coral-dim)] border-[var(--coral)]',       badge: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
    generalization: { label: 'Generalization Patterns', color: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  };

  return (
    <div className="space-y-4">
      {(Object.keys(byCategory) as Array<keyof typeof byCategory>).map(cat => {
        const items = byCategory[cat];
        if (items.length === 0) return null;
        const cfg = categoryConfig[cat];
        return (
          <div key={cat} className={` border p-4 ${cfg.color}`}>
            <p className="text-xs font-semibold text-[var(--text-sec)] mb-3">{cfg.label}</p>
            <div className="space-y-3">
              {items.sort((a, b) => b.count - a.count).map(p => (
                <div key={p.type} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-[var(--text-pri)]">{p.type}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.badge}`}>
                      {p.count}×
                    </span>
                  </div>
                  {p.examples.slice(0, 2).map((ex, i) => (
                    <p key={i} className="text-xs text-[var(--text-ter)] italic mt-1">"{ex}"</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {patterns.length === 0 && (
        <p className="text-sm text-[var(--text-ter)]">No patterns detected yet.</p>
      )}
    </div>
  );
}

function CoachingSection({
  strengths,
  blindSpots,
  growthAreas,
}: {
  strengths: string[];
  blindSpots: string[];
  growthAreas: string[];
}) {
  return (
    <div className="space-y-4">
      {strengths.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-[var(--green)]" />
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">Consistent Strengths</h4>
          </div>
          <div className="space-y-2">
            {strengths.map(s => (
              <div key={s} className="bg-[var(--green-dim)] border border-[var(--green)] px-3 py-2">
                <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{s}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {blindSpots.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-[var(--mustard)]" />
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">Persistent Blind Spots</h4>
          </div>
          <div className="space-y-2">
            {blindSpots.map(b => (
              <div key={b} className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2">
                <p className="text-sm text-[var(--mustard)]">{b}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {growthAreas.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-[var(--text-ter)]" />
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">Growth Areas</h4>
          </div>
          <div className="space-y-2">
            {growthAreas.map(g => (
              <div key={g} className="bg-[var(--slate-dim)] border border-[var(--slate)] px-3 py-2">
                <p className="text-sm text-[var(--text-sec)]">{g}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TranscriptHistory({
  transcripts,
  analyses,
  speakerName,
}: {
  transcripts: LexiconTranscript[];
  analyses: SpeakerAnalysis[];
  speakerName: string;
}) {
  const analysisByTranscript = new Map(analyses.map(a => [a.transcriptId, a]));

  const relevant = transcripts.filter(
    t => t.speakers.some(s => s.toLowerCase() === speakerName.toLowerCase())
  );

  if (relevant.length === 0) return null;

  return (
    <div className="divide-y divide-[var(--rule)]">
      {relevant.map(t => {
        const analysis = analysisByTranscript.get(t.id);
        const date = t.meetingDate
          ? new Date(t.meetingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : null;
        return (
          <div key={t.id} className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--text-pri)]">{t.title}</p>
                {date && <p className="text-xs text-[var(--text-ter)] mt-0.5">{date}</p>}
              </div>
              {analysis && (
                <div className="flex items-center gap-3">
                  {(['STR', 'MOM', 'AGN', 'SPE', 'CLR', 'FLX'] as const).map((abbr, i) => {
                    const vals = [
                      analysis.strategicIndex,
                      analysis.momentumScore,
                      analysis.agencyScore,
                      analysis.specificityScore,
                      analysis.clarityScore,
                      analysis.flexibilityScore,
                    ];
                    const v = vals[i];
                    return (
                      <div key={abbr} className="text-center">
                        <div className={`text-xs font-bold font-mono ${scoreColor(v)}`}>{v ?? '—'}</div>
                        <div className="text-xs text-[var(--text-ter)]">{abbr}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {analysis?.notableQuotes && analysis.notableQuotes.length > 0 && (
              <p className="text-xs text-[var(--text-ter)] italic mt-2">
                "{analysis.notableQuotes[0]}"
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Archetype badge ───────────────────────────────────────────────────────────

const ARCHETYPE_COLORS: Record<string, string> = {
  Executor:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
  Strategist: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  Analyst:    'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  Visionary:  'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  Connector:  'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  Challenger: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
  Operator:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LexiconSpeakerProfile() {
  const { speakerName: encodedName } = useParams<{ speakerName: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useTenantStore();
  const workspaceId = currentWorkspace?.id ?? '';

  const speakerName = decodeURIComponent(encodedName ?? '');

  const [profile, setProfile] = useState<SpeakerProfile | null>(null);
  const [transcripts, setTranscripts] = useState<LexiconTranscript[]>([]);
  const [allAnalyses, setAllAnalyses] = useState<SpeakerAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'patterns' | 'coaching' | 'history'>('overview');

  useEffect(() => {
    if (!workspaceId || !speakerName) return;

    async function load() {
      setLoading(true);

      const [profResult, txResult] = await Promise.all([
        getSpeakerProfile(workspaceId, speakerName),
        listTranscripts(workspaceId),
      ]);

      if (profResult.error) {
        setError(profResult.error);
        setLoading(false);
        return;
      }

      setProfile(profResult.data);
      setTranscripts(txResult.data);

      // Load analyses for all complete transcripts featuring this speaker
      const relevantTx = txResult.data.filter(
        t =>
          t.analysisStatus === 'complete' &&
          t.speakers.some(s => s.toLowerCase() === speakerName.toLowerCase())
      );

      const analysisArrays = await Promise.all(
        relevantTx.map(t => getAnalysesForTranscript(t.id))
      );

      const speakerAnalyses = analysisArrays
        .flatMap(r => r.data)
        .filter(a => a.speakerName.toLowerCase() === speakerName.toLowerCase());

      setAllAnalyses(speakerAnalyses);
      setLoading(false);
    }

    load();
  }, [workspaceId, speakerName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-5 h-5 text-[var(--text-ter)] animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-[var(--coral)] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[var(--coral)]">Profile not found</p>
            <p className="text-xs text-[var(--coral)] mt-0.5">
              {error ?? `No profile found for "${speakerName}".`}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/prioritization/lexicon')}
          className="mt-4 flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Lexicon
        </button>
      </div>
    );
  }

  const archetypeColor = ARCHETYPE_COLORS[profile.archetype ?? ''] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]';
  const archetypeDesc = profile.archetype
    ? ARCHETYPE_DESCRIPTIONS[profile.archetype]
    : null;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: <TrendingUp className="w-3.5 h-3.5" /> },
    { key: 'patterns', label: 'Patterns', icon: <Brain className="w-3.5 h-3.5" /> },
    { key: 'coaching', label: 'Coaching', icon: <Lightbulb className="w-3.5 h-3.5" /> },
    { key: 'history', label: 'History', icon: <BookOpen className="w-3.5 h-3.5" /> },
  ] as const;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate('/prioritization/lexicon')}
          className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Lexicon
        </button>

        {/* Profile header */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-xl font-semibold text-[var(--text-pri)]">{profile.speakerName}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-[var(--text-ter)]">
                  {profile.transcriptCount} transcript{profile.transcriptCount !== 1 ? 's' : ''}
                </span>
                <span className="text-[var(--text-ter)]">·</span>
                <span className="text-sm text-[var(--text-ter)]">
                  {(profile.totalWordCount / 1000).toFixed(1)}k words analyzed
                </span>
              </div>
            </div>
            {profile.archetype && profile.archetype !== 'Undefined' && (
              <span className={`text-sm font-semibold px-3 py-1 rounded-full border ${archetypeColor}`}>
                {profile.archetype}
              </span>
            )}
          </div>

          {archetypeDesc && (
            <p className="text-sm text-[var(--text-sec)] mb-5 bg-[var(--surface)] px-4 py-3 border border-[var(--rule)]">
              {archetypeDesc}
            </p>
          )}

          {/* 6-stat "batting card" */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {DIMENSIONS.map(dim => {
              const val = profile[dim.key];
              return (
                <div key={dim.key} className="text-center p-3 bg-[var(--surface)]">
                  <div className={`text-2xl font-bold font-mono ${scoreColor(val)}`}>
                    {val !== null ? Math.round(val as number) : '—'}
                  </div>
                  <div className="text-xs font-semibold text-[var(--text-ter)] mt-0.5">{dim.abbr}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5">{dim.label}</div>
                </div>
              );
            })}
          </div>

          {/* Action metrics */}
          {profile.avgActionCommitmentRate !== null && (
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-[var(--rule)]">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[var(--mustard)]" />
                <span className="text-sm text-[var(--text-sec)]">
                  <span className="font-semibold text-[var(--text-pri)]">
                    {Math.round(profile.avgActionCommitmentRate)}%
                  </span>{' '}
                  action commitment rate
                </span>
              </div>
              {profile.topTopics.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {profile.topTopics.slice(0, 5).map(t => (
                    <span key={t} className="text-xs bg-[var(--hover-tint)] text-[var(--text-ter)] px-1.5 py-0.5 rounded">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-5">
          {tabs.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 text-sm font-medium px-4 py-1.5  transition-colors ${
                activeTab === t.key
                  ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-5">Dimension Breakdown</h2>
            <div className="space-y-6">
              {DIMENSIONS.map(dim => (
                <ScoreMeter
                  key={dim.key}
                  value={profile[dim.key] !== null ? Math.round(profile[dim.key] as number) : null}
                  label={dim.label}
                  description={dim.description}
                  abbr={dim.abbr}
                />
              ))}
            </div>
            {profile.transcriptCount < 3 && (
              <div className="mt-6 bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-3">
                <p className="text-xs text-[var(--text-sec)]">
                  Profile accuracy improves with more transcripts. Add at least 3 to establish
                  reliable patterns. Current: {profile.transcriptCount}.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-2">NLP Meta-Model Patterns</h2>
            <p className="text-xs text-[var(--text-ter)] mb-5">
              Patterns accumulated across all analyzed transcripts for this speaker.
            </p>
            <PatternSection patterns={profile.topPatterns} />
          </div>
        )}

        {activeTab === 'coaching' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-5">Coaching Profile</h2>
            <CoachingSection
              strengths={profile.consistentStrengths}
              blindSpots={profile.persistentBlindSpots}
              growthAreas={profile.growthAreas}
            />
            {profile.consistentStrengths.length === 0 &&
             profile.persistentBlindSpots.length === 0 &&
             profile.growthAreas.length === 0 && (
              <p className="text-sm text-[var(--text-ter)]">
                Coaching insights will populate as more transcripts are analyzed.
              </p>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--rule)]">
              <h2 className="text-sm font-semibold text-[var(--text-sec)]">Transcript History</h2>
            </div>
            <TranscriptHistory
              transcripts={transcripts}
              analyses={allAnalyses}
              speakerName={speakerName}
            />
          </div>
        )}
      </div>
    </div>
  );
}
